/**
 * INC-117302: Variant of MixedGroupedOutrightsItem for tournaments with large outright payloads.
 *
 * Difference from MixedGroupedOutrightsItem:
 * - Instead of subscribing to all odds on mount, it first fetches betting types via RPC (#tournamentBettingTypes)
 *   and renders them as a collapsed accordion (TournamentBettingTypesAccordion).
 * - Each accordion item subscribes to `tournament-odds/{bettingTypeId}` only when expanded,
 *   and unsubscribes on collapse — avoiding the 4.2MB WS payload on initial load.
 * - When a market group is selected via MarketGroupsTournamentOutrights nav,
 *   it falls back to the standard TournamentOutrights behaviour (same as original item).
 *
 * Controlled by outrightBettingTypes.tournamentIds in operator config.
 * Original MixedGroupedOutrightsItem is untouched and used for all other tournaments.
 */
import React, { memo, useCallback, useState } from 'react';
import { MixedGroupedTournamentModel } from '../../mixed-grouped.types';
import { BemMethod, createBem } from '@om-fe/utils';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import { MixedGroupTournamentHeader } from '../tournament-header/mixed-group-tournament-header';
import { UseSimpleStore, useSimpleStoreItem } from '@om-fe/store';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';
import MarketGroupsTournamentOutrights from '@om-fe/core/components/market-groups/market-groups-tournament-outrights.tsx';
import TournamentOutrights from '@om-fe/core/components/tournament-outrights/tournament-outrights';
import TournamentBettingTypesAccordion from '@om-fe/core/components/tournament-outrights/tournament-betting-types-accordion';
import { GroupKeys } from '@om-fe/core/components/market-groups/market-groups.constants.ts';
import { DisciplinesType } from '@om-fe/models/dist/models/types/disciplines';

const bem: BemMethod = createBem('MixedGroupedOutrightsItem');

export interface MixedGroupedBettingTypesItemProps {
    readonly useStore: UseSimpleStore<MixedGroupedTournamentModel>;
    readonly id: string;
    readonly sportPageType: DisciplineRouteType;
    readonly count: number;
}

export const MixedGroupedBettingTypesItem = memo(function MixedGroupedBettingTypesItemComponent({
    useStore,
    id,
    sportPageType,
    count,
}: MixedGroupedBettingTypesItemProps) {
    const initiallyOpen: boolean = count === 0;
    const [show, setShow] = useState<boolean>(initiallyOpen);
    const [hasOpened, setHasOpened] = useState<boolean>(initiallyOpen);
    const [activeMarketGroup, setActiveMarketGroup] = useState<string | undefined>(undefined);
    const [loadedMarketGroups, setLoadedMarketGroups] = useState<string[]>([]);
    const tournament: MixedGroupedTournamentModel = useSimpleStoreItem<MixedGroupedTournamentModel>(useStore, id);
    const config: OMFE2ConfigModel = getConfig();
    const showEndTime: boolean | undefined = config.matchListConfig?.outrights?.showEndTime;
    const dateFormat: string | undefined = config.dateFormat?.FormatFullDateTime;
    const enabledDash: boolean = config.bannerMatrix?.enabledDashOnPriceboost || false;
    const outrightMarketGroup: boolean = getConfig().outrightMarketGroup ?? false;

    const toggle = useCallback(() => {
        setHasOpened(true);
        setShow((value: boolean): boolean => !value);
    }, []);

    const handleActiveMarketGroup = useCallback((marketGroup: string): void => {
        const newGroup = marketGroup === GroupKeys.All ? undefined : marketGroup;
        setActiveMarketGroup(newGroup);
        if (newGroup) {
            setLoadedMarketGroups((current) =>
                current.includes(newGroup) ? current : [...current, newGroup]
            );
        }
    }, []);

    if ((!tournament.isTopLevelTournament && !tournament.numberOfOutrightMarkets) || (tournament.isTopLevelTournament && !tournament.numberOfOutrightMarkets && !tournament.numberOfOutrightMarketsForSubtournaments)) {
        return null;
    }

    return (
        <div className={bem()}>
            <MixedGroupTournamentHeader
                useStore={useStore}
                sportPageType={sportPageType}
                id={id}
                open={show}
                onToggle={toggle}
            />
            {(show || hasOpened) && (
                <div className={bem('Content')} hidden={!show}>
                    {outrightMarketGroup &&
                        <MarketGroupsTournamentOutrights
                            tournamentId={tournament.id}
                            instanceId={`MixedMarketGroupOutrightsItem-${tournament.id}`}
                            activeMarketGroupId={activeMarketGroup}
                            setActiveMarketGroup={handleActiveMarketGroup}
                        />
                    }
                    {(!outrightMarketGroup || !activeMarketGroup) && (
                        <TournamentBettingTypesAccordion
                            tournamentId={tournament.id}
                            instanceId={`BettingTypesAccordion-${tournament.id}`}
                            disciplineId={tournament.sportId}
                            showEndTime={showEndTime}
                            endTime={tournament.endTime}
                            columnByBreakpoint={true}
                        />
                    )}
                    {outrightMarketGroup && loadedMarketGroups.map((marketGroup: string) => (
                        <div key={marketGroup} hidden={marketGroup !== activeMarketGroup}>
                            <TournamentOutrights
                                instanceId={`MixedGroupedBettingTypesItem-${id}-${marketGroup}`}
                                tournamentId={tournament.id}
                                disciplineId={tournament.sportId}
                                showEndTime={showEndTime}
                                endTime={tournament.endTime}
                                dateFormat={dateFormat}
                                activeMarketGroup={marketGroup}
                                columnByBreakpoint={true}
                                showDash={enabledDash && tournament.sportId === DisciplinesType.PriceBoost}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});
